import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { money } from "../lib/pricing";
import { openAuthFile } from "../lib/fileOpen";
import { Button } from "../components/ui/button";
import { Spinner } from "../components/hs/hsUtils";
import { ArrowLeft, Money, FilePdf } from "@phosphor-icons/react";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");

/** Engineer's payslips (approved pay runs for the employee record linked to this login). */
export default function MyPayslipsPage() {
  const [d, setD] = useState(null);
  const [p60s, setP60s] = useState([]);
  const [p45, setP45] = useState(null);
  useEffect(() => { API.get("/my/payslips").then((r) => setD(r.data)).catch(() => setD({ employee: null, payslips: [] })); API.get("/my/p60s").then((r) => setP60s(r.data.p60s || [])).catch(() => setP60s([])); API.get("/my/p45").then((r) => setP45(r.data.p45)).catch(() => setP45(null)); }, []);
  const latest = d?.payslips?.[0];
  return (
    <div className="pb-6" data-testid="my-payslips-page">
      <div className="sticky top-0 z-30 bg-card/95 backdrop-blur border-b border-border">
        <div className="max-w-3xl mx-auto px-3 py-2.5 flex items-center gap-2">
          <Link to="/my-work" className="flex items-center gap-1 px-2 py-2 -ml-2 rounded-md hover:bg-muted text-sm font-medium min-h-[44px]" data-testid="my-payslips-back"><ArrowLeft className="h-5 w-5" /><span className="hidden sm:inline">My work</span></Link>
          <h1 className="text-sm font-semibold flex-1 text-center sm:text-left flex items-center gap-2"><Money size={16} className="text-primary" /> My payslips</h1>
        </div>
      </div>
      <div className="p-4 md:p-6 space-y-4 max-w-3xl mx-auto">
        {!d ? <Spinner /> : !d.employee ? (
          <div className="rounded-xl border border-dashed border-border p-10 text-center" data-testid="my-payslips-none">
            <Money size={36} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm font-medium">You're not linked to a payroll record yet</p>
            <p className="text-xs text-muted-foreground mt-1">Ask the office to link your login on your employee record under Accounts → Payroll.</p>
          </div>
        ) : (
          <>
            <div className="rounded-xl border border-border bg-card p-4" data-testid="my-payslips-summary">
              <p className="text-xs text-muted-foreground">{d.employee.name} · {d.employee.payroll_id} · tax code {d.employee.tax_code}</p>
              {latest ? <><p className="text-3xl font-bold font-[Chivo] mt-1" data-testid="my-payslips-latest-net">{money(latest.net)}</p><p className="text-xs text-muted-foreground">Latest net pay · paid {fmt(latest.pay_date)} · year to date gross {money(latest.ytd?.gross || 0)}, tax {money(latest.ytd?.tax || 0)}</p></> : <p className="text-sm mt-1">No payslips yet.</p>}
            </div>
            {p45 && (
              <div className="rounded-xl border border-border bg-card p-3 flex items-center gap-3" data-testid="my-p45">
                <div className="min-w-0 flex-1"><p className="text-sm font-semibold">Your P45 <span className="text-xs text-muted-foreground font-normal">· left {new Date(p45.leaving_date).toLocaleDateString("en-GB")}</span></p><p className="text-xs text-muted-foreground">Tax year {p45.tax_year_label} · pay to date {money(p45.total_pay)} · tax {money(p45.total_tax)} · code {p45.tax_code}. Give Parts 2 & 3 to your next employer.</p></div>
                <Button size="sm" variant="outline" className="h-9" onClick={() => openAuthFile("/my/p45.pdf")} data-testid="my-p45-pdf"><FilePdf size={14} className="mr-1" /> P45</Button>
              </div>
            )}
            {p60s.length > 0 && (
              <div className="rounded-xl border border-border bg-card p-3 space-y-2" data-testid="my-p60s">
                <p className="text-sm font-semibold">Your P60s <span className="text-xs text-muted-foreground font-normal">· end of year certificates, keep them safe</span></p>
                {p60s.map((p) => (
                  <div key={p.tax_year} className="flex items-center gap-3" data-testid={`my-p60-${p.tax_year}`}>
                    <div className="min-w-0 flex-1"><p className="text-sm font-medium">Tax year {p.label}</p><p className="text-xs text-muted-foreground">Total pay {money(p.total_pay)} · tax {money(p.total_tax)}</p></div>
                    <Button size="sm" variant="outline" className="h-9" onClick={() => openAuthFile(`/my/p60s/${p.tax_year}.pdf`)} data-testid={`my-p60-pdf-${p.tax_year}`}><FilePdf size={14} className="mr-1" /> P60</Button>
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-2" data-testid="my-payslips-list">
              {d.payslips.map((p) => (
                <div key={p.run_id} className="rounded-xl border border-border bg-card p-3 flex items-center gap-3" data-testid={`my-payslip-${p.run_id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{fmt(p.period_start)} – {fmt(p.period_end)}</p>
                    <p className="text-xs text-muted-foreground">Paid {fmt(p.pay_date)} · gross {money(p.gross)} · tax {money(p.tax)} · NI {money(p.ni_employee)}{p.pension_employee ? ` · pension ${money(p.pension_employee)}` : ""}</p>
                  </div>
                  <span className="font-semibold tabular-nums">{money(p.net)}</span>
                  <Button size="sm" variant="outline" className="h-9" onClick={() => openAuthFile(`/my/payslips/${p.run_id}.pdf`)} data-testid={`my-payslip-pdf-${p.run_id}`}><FilePdf size={14} className="mr-1" /> PDF</Button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
